// src/pages/UserPages/ServicePage.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import { useThemeMode } from "../../components/ThemeModeContext";

function ServicePage() {
  const { serviceId } = useParams();
  const token = localStorage.getItem("authToken");

  // Estados gerais
  const [service, setService] = useState(null);
  const [sector, setSector] = useState(null);
  const [company, setCompany] = useState(null);
  const [date, setDate] = useState("");
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [creatingAppointment, setCreatingAppointment] = useState(false);

  // Estados de Snackbar (caso queira usar)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Dark/Light Mode
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Busca detalhes do serviço, setor e companhia
  const fetchServiceDetails = async () => {
    setLoading(true);
    try {
      const [serviceResponse, sectorResponse, companyResponse] = await Promise.all([
        axiosInstance.get(`service/${serviceId}/`),
        axiosInstance.get(`service/sector/${serviceId}/`),
        axiosInstance.get(`service/company/${serviceId}/`),
      ]);

      if (
        serviceResponse.data.status === "success" &&
        sectorResponse.data.status === "success" &&
        companyResponse.data.status === "success"
      ) {
        setService(serviceResponse.data.data);
        setSector(sectorResponse.data.data);
        setCompany(companyResponse.data.data);
      } else {
        Swal.fire("Error", "Erro ao buscar detalhes do serviço", "error");
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes do serviço", error);
      Swal.fire("Error", "Algum erro ocorreu ao buscar detalhes do serviço", "error");
    } finally {
      setLoading(false);
    }
  };

  // Busca slots ocupados de um dia
  const fetchOccupiedSlots = async () => {
    if (!date) return;
    try {
      const response = await axiosInstance.get(
        `service/${serviceId}/occupied-slots/${date.split("T")[0]}/`
      );
      if (response.data.status === "success") {
        setOccupiedSlots(response.data.occupied_slots);
      } else {
        setOccupiedSlots([]);
      }
    } catch (error) {
      console.error("Erro ao buscar slots ocupados", error);
      Swal.fire("Error", "Erro ao buscar slots ocupados", "error");
    }
  };

  useEffect(() => {
    fetchServiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    fetchOccupiedSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Cria agendamento
  const handleCreateAppointment = async () => {
    if (!token) {
      Swal.fire("Não autorizado", "Faça login para agendar um serviço", "error");
      return;
    }

    if (!date) {
      Swal.fire("Erro de validação", "Selecione uma data para agendar", "error");
      return;
    }

    setCreatingAppointment(true);
    try {
      const response = await axiosInstance.post(
        `service/appointment/${token}/${serviceId}/`,
        { date },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        Swal.fire("Sucesso", response.data.message, "success");
      } else {
        Swal.fire("Erro", response.data.message, "error");
      }
    } catch (error) {
      console.error("Erro ao criar agendamento", error);
      Swal.fire("Error", "Falha ao criar agendamento", "error");
    } finally {
      setCreatingAppointment(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let result = "";
    if (hours > 0) result += `${hours}h `;
    if (mins > 0) result += `${mins}min`;
    return result.trim() || "0min";
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        
        {/* Navbar do Usuário */}
        <Navbar />

        {/* Conteúdo Principal */}
        {loading ? (
          <Box flex={1} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : (
          <Box flex={1}>
            <Container sx={{ py: 4 }}>
              {/* Banner e Descrição do Serviço */}
              <Paper elevation={3} sx={{ mb: 4 }}>
                {company?.banner && (
                  <CardMedia
                    component="img"
                    height="250"
                    image={company.banner}
                    alt={`${service?.name} Banner`}
                  />
                )}
                <CardContent>
                  <Typography variant="h4" align="center" gutterBottom>
                    {service?.name}
                  </Typography>
                  <Box mt={2}>
                    <ReactMarkdown>
                      {service?.description || "Sem descrição disponível."}
                    </ReactMarkdown>
                  </Box>
                </CardContent>
              </Paper>

              {/* Tabs */}
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                centered
                indicatorColor="primary"
                textColor="primary"
                sx={{ mb: 4 }}
              >
                <Tab label="Service Details" />
                <Tab label="Sector Info" />
                <Tab label="Company Info" />
              </Tabs>

              {/* Conteúdo das Tabs */}
              <Box>
                {/* Service Details */}
                {tabValue === 0 && service && (
                  <Paper elevation={3} sx={{ mb: 4, p: 3 }}>
                    <Typography variant="h6">Preço: ${service.price}</Typography>
                    <Typography variant="h6">
                      Duração: {formatDuration(service.duration)}
                    </Typography>
                    <Typography variant="h6">Tempo de espera entre as sessões: {service.wait_time} dias </Typography>
                    <Typography variant="h6">Quantidade de sessões e retornos: {service.quantity}</Typography>
                  </Paper>
                )}

                {/* Sector Info */}
                {tabValue === 1 && sector && (
                  <Paper elevation={3} sx={{ mb: 4, p: 3 }}>
                    <Typography variant="h6">setor: {sector.name}</Typography>
                    <Box mt={2}>
                      <ReactMarkdown>
                        {sector.description || "Sem descrição disponível."}
                      </ReactMarkdown>
                    </Box>
                    <Typography variant="body2">Abre as: {sector.open_time || "N/A"}</Typography>
                    <Typography variant="body2">Fecha as: {sector.close_time || "N/A"}</Typography>
                  </Paper>
                )}

                {/* Company Info */}
                {tabValue === 2 && company && (
                  <Paper elevation={3} sx={{ mb: 4, p: 3 }}>
                    <Typography variant="h6">Empresa: {company.name}</Typography>
                    <Typography variant="body1">{company.description}</Typography>
                    <Typography variant="body2">Endereço: {company.address}</Typography>
                    <Typography variant="body2">Cidade: {company.city}</Typography>
                    <Typography variant="body2">Telefone: {company.phone}</Typography>
                  </Paper>
                )}
              </Box>

              {/* Occupied Slots */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Horas ocupadas para a data selecionada
                </Typography>
                {occupiedSlots.length > 0 ? (
                  <List>
                    {occupiedSlots.map((slot, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`Inicio: ${slot.start_time}, Fim: ${slot.end_time}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>Sem horarios ocupados para a data selecionada</Typography>
                )}
              </Box>
            </Container>

            {/* Área Inferior Fixa para Marcar Agendamento */}
            <Box
              position="sticky"
              bottom={0}
              left={0}
              width="100%"
              bgcolor="background.paper"
              p={2}
              borderTop="1px solid #ccc"
              zIndex={1000}
              mt="auto"
            >
              <Container>
                <Typography variant="h6" gutterBottom>
                  Criar Agendamento
                </Typography>
                <TextField
                  label="Select Date"
                  type="datetime-local"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateAppointment}
                  disabled={creatingAppointment}
                  fullWidth
                >
                  {creatingAppointment ? <CircularProgress size={20} color="inherit" /> : "Book Now"}
                </Button>
              </Container>
            </Box>
          </Box>
        )}

        {/* FeedbackButton (DarkMode + Feedback) */}
        <FeedbackButton />

        {/* Footer */}
        <Footer />

        {/* Snackbar para feedback, se desejar */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={() => setOpenSnackbar(false)}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
}

export default ServicePage;
